import { useState } from 'react';
import {
  Stack,
  Paper,
  Title,
  Text,
  Group,
  Button,
  Checkbox,
  SimpleGrid,
  Progress,
  Alert,
  List,
  Badge,
  Card,
  ThemeIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconDownload,
  IconFileZip,
  IconCheck,
  IconX,
  IconLoader,
  IconAlertCircle,
  IconFile,
} from '@tabler/icons-react';
import {
  useStartExport,
  useExportProgress,
  useDownloadExport,
  useCancelExport,
  useQuickExport,
  useGoogleAdsAccount,
} from '@/hooks/useGoogleAds';
import type { DateRangeOption, ExportOptions } from '@/types/googleAds';

interface ExportTabProps {
  customerId: string;
  dateRange: DateRangeOption;
}

const EXPORT_FILES = [
  { key: 'account', label: 'Konto', description: 'Allgemeine Kontoinformationen' },
  { key: 'campaigns', label: 'Kampagnen', description: 'Alle Kampagnen mit Metriken' },
  { key: 'assetGroups', label: 'Asset Groups', description: 'Performance Max Asset Groups' },
  { key: 'ads', label: 'Anzeigen', description: 'Alle Anzeigen mit Headlines und Descriptions' },
  { key: 'assets', label: 'Assets', description: 'Alle Asset-Typen' },
  { key: 'headlines', label: 'Headlines', description: 'Extrahierte Anzeigen-Headlines' },
  { key: 'descriptions', label: 'Descriptions', description: 'Extrahierte Anzeigen-Descriptions' },
  { key: 'images', label: 'Bilder', description: 'Bild-Assets mit URLs und Größen' },
  { key: 'videos', label: 'Videos', description: 'Video-Assets mit YouTube-IDs' },
  { key: 'keywords', label: 'Keywords', description: 'Keywords mit Quality Score' },
  { key: 'negativeKeywords', label: 'Negative Keywords', description: 'Alle negativen Keywords' },
  { key: 'searchTerms', label: 'Suchbegriffe', description: 'Suchbegriffe mit Metriken' },
  { key: 'locations', label: 'Standorte', description: 'Geo-Targeting-Einstellungen' },
  { key: 'audiences', label: 'Zielgruppen', description: 'Alle Zielgruppen-Targets' },
  { key: 'recommendations', label: 'Empfehlungen', description: 'Google Ads Empfehlungen' },
  { key: 'recommendationImpact', label: 'Empfehlungs-Impact', description: 'Geschätzte Auswirkungen' },
  { key: 'conversionActions', label: 'Conversion Actions', description: 'Conversion-Tracking-Setup' },
  { key: 'conversionStats', label: 'Conversion Stats', description: 'Conversion-Statistiken' },
  { key: 'budgets', label: 'Budgets', description: 'Kampagnenbudgets' },
  { key: 'biddingStrategies', label: 'Gebotsstrategien', description: 'Bidding Strategies' },
  { key: 'extensions', label: 'Extensions', description: 'Anzeigenerweiterungen' },
  { key: 'landingPages', label: 'Landing Pages', description: 'Landing Page Performance' },
  { key: 'performance', label: 'Performance', description: 'Performance für alle Zeiträume' },
  { key: 'qualitySignals', label: 'Qualitätssignale', description: 'Quality Score Verteilung' },
  { key: 'labels', label: 'Labels', description: 'Alle Labels' },
  { key: 'shopping', label: 'Shopping', description: 'Merchant Center Produkte' },
  { key: 'changeHistory', label: 'Änderungsverlauf', description: 'Letzte 12 Monate' },
];

function exportErrorMessage(err: unknown): string {
  const responseData = (err as { response?: { data?: { message?: string; error?: string } } })
    ?.response?.data;
  return responseData?.message || responseData?.error || 'Unbekannter Fehler';
}

export function ExportTab({ customerId, dateRange }: ExportTabProps) {
  const { data: account } = useGoogleAdsAccount(customerId);
  const startExport = useStartExport();
  const downloadExport = useDownloadExport();
  const cancelExport = useCancelExport();
  const quickExport = useQuickExport();

  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [includeChangeHistory, setIncludeChangeHistory] = useState(true);
  const [changeHistoryMonths, setChangeHistoryMonths] = useState(12);

  const { data: progress, isLoading: progressLoading } = useExportProgress(activeJobId ?? undefined, !!activeJobId);

  const handleStartExport = async () => {
    const options: ExportOptions = {
      customerId,
      dateRange,
      includeChangeHistory,
      changeHistoryMonths,
    };

    try {
      const { jobId } = await startExport.mutateAsync(options);
      setActiveJobId(jobId);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Export konnte nicht gestartet werden',
        message: exportErrorMessage(err),
      });
    }
  };

  const handleQuickExport = async () => {
    try {
      const result = await quickExport.mutateAsync({ customerId, dateRange });
      if (result.errors?.length) {
        notifications.show({
          color: 'yellow',
          title: 'Schnellexport teilweise unvollständig',
          message: result.errors.join(', '),
        });
      }
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Schnellexport fehlgeschlagen',
        message: exportErrorMessage(err),
      });
    }
  };

  const handleDownload = () => {
    if (activeJobId) {
      downloadExport.mutate(activeJobId);
    }
  };

  const handleCancel = () => {
    if (activeJobId) {
      cancelExport.mutate(activeJobId);
      setActiveJobId(null);
    }
  };

  const isExporting = progress?.status === 'RUNNING' || progress?.status === 'PENDING';
  const isCompleted = progress?.status === 'COMPLETED';
  const isFailed = progress?.status === 'FAILED';

  return (
    <Stack gap="lg">
      <Paper withBorder p="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={4}>Vollständiger Export</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Exportiere alle Google Ads Daten für {account?.name ?? customerId}
            </Text>
          </div>
          <Group>
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              onClick={handleQuickExport}
              loading={quickExport.isPending}
              disabled={isExporting}
            >
              Schnellexport
            </Button>
            <Button
              leftSection={<IconFileZip size={16} />}
              onClick={handleStartExport}
              loading={startExport.isPending}
              disabled={isExporting}
            >
              Vollständigen Export starten
            </Button>
          </Group>
        </Group>
      </Paper>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Paper withBorder p="md">
          <Title order={5} mb="md">
            Export-Optionen
          </Title>
          <Stack gap="sm">
            <Checkbox
              label="Änderungsverlauf einschließen"
              description="Change History der letzten Monate"
              checked={includeChangeHistory}
              onChange={(e) => setIncludeChangeHistory(e.currentTarget.checked)}
            />
            {includeChangeHistory && (
              <Group>
                <Text size="sm">Monate:</Text>
                {[3, 6, 12].map((months) => (
                  <Button
                    key={months}
                    variant={changeHistoryMonths === months ? 'filled' : 'light'}
                    size="xs"
                    onClick={() => setChangeHistoryMonths(months)}
                  >
                    {months}
                  </Button>
                ))}
              </Group>
            )}
          </Stack>
        </Paper>

        <Paper withBorder p="md">
          <Title order={5} mb="md">
            Exportierte Dateien
          </Title>
          <Text size="sm" c="dimmed" mb="sm">
            {EXPORT_FILES.length} JSON-Dateien + audit-summary.json
          </Text>
          <List size="sm" spacing={4}>
            {EXPORT_FILES.slice(0, 8).map((file) => (
              <List.Item key={file.key} icon={<IconFile size={14} />}>
                {file.label}
              </List.Item>
            ))}
            <List.Item icon={<IconFile size={14} />}>
              ... und {EXPORT_FILES.length - 8} weitere
            </List.Item>
          </List>
        </Paper>
      </SimpleGrid>

      {(activeJobId || isExporting) && (
        <Paper withBorder p="md">
          <Title order={5} mb="md">
            Export-Fortschritt
          </Title>

          {progressLoading && !progress && (
            <Group>
              <IconLoader size={20} className="animate-spin" />
              <Text size="sm">Lade Status...</Text>
            </Group>
          )}

          {progress && (
            <Stack gap="md">
              <Group justify="space-between">
                <Group>
                  <Badge
                    color={
                      progress.status === 'COMPLETED'
                        ? 'green'
                        : progress.status === 'FAILED'
                          ? 'red'
                          : progress.status === 'RUNNING'
                            ? 'blue'
                            : 'gray'
                    }
                    size="lg"
                  >
                    {progress.status === 'COMPLETED'
                      ? 'Abgeschlossen'
                      : progress.status === 'FAILED'
                        ? 'Fehlgeschlagen'
                        : progress.status === 'RUNNING'
                          ? 'Läuft...'
                          : 'Wartend'}
                  </Badge>
                  {progress.currentStep && (
                    <Text size="sm" c="dimmed">
                      {progress.currentStep}
                    </Text>
                  )}
                </Group>
                <Text size="sm" fw={500}>
                  {progress.progress.toFixed(0)}%
                </Text>
              </Group>

              <Progress value={progress.progress} size="lg" animated={isExporting} />

              {progress.files.length > 0 && (
                <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }}>
                  {progress.files.map((file) => (
                    <Card key={file.name} withBorder padding="xs">
                      <Group gap="xs" wrap="nowrap">
                        <ThemeIcon
                          size="sm"
                          variant="light"
                          color={
                            file.status === 'COMPLETED'
                              ? 'green'
                              : file.status === 'FAILED'
                                ? 'red'
                                : 'gray'
                          }
                        >
                          {file.status === 'COMPLETED' ? (
                            <IconCheck size={12} />
                          ) : file.status === 'FAILED' ? (
                            <IconX size={12} />
                          ) : (
                            <IconLoader size={12} />
                          )}
                        </ThemeIcon>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <Text size="xs" fw={500} lineClamp={1}>
                            {file.name}
                          </Text>
                          {file.recordCount !== null && (
                            <Text size="xs" c="dimmed">
                              {file.recordCount} Einträge
                            </Text>
                          )}
                          {file.error && (
                            <Text size="xs" c="red">
                              {file.error}
                            </Text>
                          )}
                        </div>
                      </Group>
                    </Card>
                  ))}
                </SimpleGrid>
              )}

              {isFailed && progress.error && (
                <Alert icon={<IconAlertCircle size={16} />} color="red" title="Export fehlgeschlagen">
                  {progress.error}
                </Alert>
              )}

              <Group>
                {isExporting && (
                  <Button variant="light" color="red" onClick={handleCancel} loading={cancelExport.isPending}>
                    Abbrechen
                  </Button>
                )}
                {isCompleted && (
                  <Button
                    leftSection={<IconDownload size={16} />}
                    onClick={handleDownload}
                    loading={downloadExport.isPending}
                  >
                    Download (.zip)
                  </Button>
                )}
              </Group>
            </Stack>
          )}
        </Paper>
      )}

      <Paper withBorder p="md">
        <Title order={5} mb="md">
          Export-Format
        </Title>
        <Text size="sm" c="dimmed" mb="md">
          Jede Datei folgt diesem Schema:
        </Text>
        <Paper withBorder p="sm" bg="gray.0">
          <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
{`{
  "generatedAt": "2024-01-15T10:30:00Z",
  "customerId": "${customerId}",
  "apiVersion": "v18",
  "data": [
    // Array mit den jeweiligen Objekten
  ]
}`}
          </pre>
        </Paper>
      </Paper>
    </Stack>
  );
}
